import { useMemo } from 'react';
import { useSettings } from '@wu/widget-core';
import { nanoid } from 'nanoid';
import { type RewardType } from '../../types';

export interface ClonedRewardType extends RewardType {
  original: RewardType;
}

export type WheelRewardType = RewardType | ClonedRewardType;

export function useWheelRewards(): WheelRewardType[] {
  const { rewards } = useSettings();
  return useMemo(() => {
    const numberOfRewards = rewards.length;

    if (numberOfRewards === 0) {
      return [];
    }

    // In order to maintain a consistent visual appearance,
    // we extend the set of rewards following certain rules.

    if ([1, 2, 4].includes(numberOfRewards)) {
      return extendRewards(rewards, 8);
    }

    if (numberOfRewards === 3) {
      return extendRewards(rewards, 6);
    }

    if (numberOfRewards === 5) {
      return extendRewards(rewards, 10);
    }

    // If there are 6 or more rewards, we don't extend the set but ensure it's even.
    // To do so, we clone the first reward and put it in the middle of the set,
    // halfing the chance of both to maintain the total chance unaffected.
    if (numberOfRewards % 2 !== 0) {
      const firstReward = rewards[0];
      const weakenedFirstReward: RewardType = {
        ...firstReward,
        chance: firstReward.chance / 2
      };
      const middleReward = cloneReward(weakenedFirstReward);
      const middleIndex = Math.floor(numberOfRewards / 2);
      return [
        weakenedFirstReward,
        ...rewards.slice(1, middleIndex),
        middleReward,
        ...rewards.slice(middleIndex)
      ];
    }

    return rewards;
  }, [rewards]);
}

export function getOriginalReward(reward: WheelRewardType): RewardType {
  return 'original' in reward ? reward.original : reward;
}

function extendRewards(
  rewards: RewardType[],
  targetLength: number
): WheelRewardType[] {
  const numberOfRewards = rewards.length;
  if (numberOfRewards === 0 || targetLength <= numberOfRewards) {
    throw new Error(
      'Invalid arguments. The number of rewards must be greater than 0 and less than the target length.'
    );
  }

  const addedRewards = Array.from(
    { length: targetLength - numberOfRewards },
    (_, index) => {
      const originalReward = rewards[index % numberOfRewards];
      return cloneReward(originalReward);
    }
  );
  return [...rewards, ...addedRewards];
}

function cloneReward(reward: RewardType): ClonedRewardType {
  return {
    ...reward,
    id: nanoid(8),
    original: reward
  };
}
